import {
  CreateButton,
  DateField,
  DeleteButton,
  List,
  RefreshButton,
  TextField,
  getDefaultSortOrder,
  useTable,
} from "@refinedev/antd";

import {
  Col,
  Divider,
  Row,
  Space,
  Table,
  Tag,
  Timeline,
  Typography,
  Alert,
  Button,
  notification,
} from "antd";
import type { ColumnsType } from "antd/es/table";

import { IResourceComponentsProps, useModal, useInvalidate } from "@refinedev/core";

import { IAvcsIncrementalUpdate } from "interfaces";
import prettyBytes from "pretty-bytes";
import React from "react";
import { DownloadIncrementalUpdate } from "./components/DownloadIncrementalUpdate";
import { DownloadOutlined, CopyOutlined } from "@ant-design/icons";

// Template URL for Digital Data Request
const GCS_VESSEL_RESOURCES_URL = process.env.REACT_APP_GCS_VESSEL_RESOURCES_URL || "https://storage.googleapis.com/emr_vessel_resources_v1";
const TEMPLATE_URL = `${GCS_VESSEL_RESOURCES_URL}/ddr_template/Digital_Data_Request.xlsx`;

export const IncrementalUpdateList: React.FC<
  IResourceComponentsProps
> = () => {
  const [record, setRecord] = React.useState<IAvcsIncrementalUpdate>();
  const invalidate = useInvalidate();

  const { tableProps, sorters, tableQuery: { isLoading, refetch }, searchFormProps, filters } =
    useTable<IAvcsIncrementalUpdate>({
      resource: "avcs-incremental-update",
      syncWithLocation: false,

      sorters: {
        initial: [
          {
            field: "id",
            order: "desc",
          },
        ]
      }
    });

  const handleRefresh = () => {
    invalidate({
      resource: "avcs-incremental-update",
      invalidates: ["list"],
    });
  };

  const columns: ColumnsType<IAvcsIncrementalUpdate> = [
    {
      title: "IMO Number",
      dataIndex: "imoNumber",
      key: "imoNumber",
      width: 150,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("imoNumber", sorters),
      sorter: true,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 200,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("title", sorters),
      sorter: true,
    },
    {
      title: "File Name",
      dataIndex: "fileName",
      key: "fileName",
      width: 200,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("fileName", sorters),
      sorter: true,
    },
    {
      title: "File Size",
      dataIndex: "fileSize",
      key: "fileSize",
      width: 150,
      className: "wrap-text",
      render: (value) => <TextField value={value ? prettyBytes(Number(value)) : null} />,
      defaultSortOrder: getDefaultSortOrder("fileSize", sorters),
      sorter: true,
    },
    {
      title: "Last Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 200,
      className: "wrap-text",
      render: (value) => (
        <DateField format="DD-MMM-YYYY hh:mm:ss A" value={value} />
      ),
      defaultSortOrder: getDefaultSortOrder("updatedAt", sorters),
      sorter: true,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <Space>
          <DownloadIncrementalUpdate
            storageUrl={record.storageUrl}
            fileName={record.fileName}
          />
          <Button
            icon={<CopyOutlined />}
            size="small"
            onClick={() => {
              navigator.clipboard.writeText(record.storageUrl);
              notification.success({
                message: "Success",
                description: "Download link copied to clipboard",
                placement: "topRight",
              });
            }}
          />
          <DeleteButton
            hideText
            size="small"
            recordItemId={record.id}
            successNotification={{
              type: "success",
              message: "Deleted the requested AVCS data file",
              description: "Deleted Successfully"
            }}
            errorNotification={{
              type: "error",
              message: "Could not delete the requested AVCS data file. Please try again.",
              description: "Failed to Delete"
            }}
            onSuccess={() => {
              // TODO: Delete the file from cloud storage bucket
            }}
          />
        </Space>
      ),
    },
  ];

  const downloadTemplate = () => {
    // Create a temporary link to download the file
    const link = document.createElement('a');
    link.href = TEMPLATE_URL;
    link.setAttribute('download', 'Digital_Data_Request.xlsx');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
      <List
        breadcrumb={false}
        headerProps={{
          // onBack: () => window.history.back(),
          title: "AVCS Data Request",
          subTitle: "request AVCS data for ECDIS",
          tags: isLoading ? (
            <Tag color="orange">loading</Tag>
          ) : (
            <Tag color="green">live</Tag>
          ),
          extra: (
            <React.Fragment>
              <Space>
                <RefreshButton 
                  resource="avcs-incremental-update"
                  onClick={handleRefresh}
                  loading={isLoading}
                />
                <Button 
                  icon={<DownloadOutlined />} 
                  onClick={downloadTemplate}
                >
                  Download DDR
                </Button>
                
                <CreateButton children="Request Update"/>
              </Space>
            </React.Fragment>
          ),
          ghost: true,
          footer: (
            <>              
              <Divider plain>
                © {new Date().getFullYear()} e-Mariner NavSolutions
              </Divider>
            </>
          ),
        }}
      >
        <Row gutter={[16, 16]} align="stretch">
          <Col xs={24} md={12} style={{ display: 'flex' }}>
            <Alert
              type="info"
              message={
                <Typography.Title level={5} style={{ color: 'inherit', margin: '0', padding: '0' }}>
                  How to request AVCS data via portal:
                </Typography.Title>
              }
              description={
                <div>
                  <Timeline style={{ paddingBottom: 0}}>
                    <Timeline.Item style={{ paddingTop: 9}}>
                      <Typography.Text strong>Step 1</Typography.Text>{" "}
                      <Button type="link" onClick={downloadTemplate}>
                        <DownloadOutlined /> Download DDR 
                      </Button>
                      and prepare request file
                    </Timeline.Item>
                    <Timeline.Item >
                      <Typography.Text strong>Step 2</Typography.Text>{" "}
                      <CreateButton type="link">
                        Request Update
                      </CreateButton>
                    </Timeline.Item>
                    <Timeline.Item style={{ paddingBottom: 0}}>
                      <Typography.Text strong>Step 3</Typography.Text> Upload digital data request file
                    </Timeline.Item>
                  </Timeline>
                </div>
              }
              showIcon
              style={{ 
                marginBottom: '9px',
                paddingBottom: '0px',
                flex: 1,
              }}
            />
          </Col>
          <Col xs={24} md={12} style={{ display: 'flex' }}>
            <Alert
              type="info"
              message={
                <Typography.Title level={5} style={{ color: 'inherit', margin: '0', padding: '0' }}>
                  How to request AVCS data via email:
                </Typography.Title>
              }
              description={
                <div>
                  <Timeline style={{ paddingBottom: 0}}>
                    <Timeline.Item style={{ paddingTop: 9}}>
                      <Typography.Text strong>Step 1</Typography.Text>{" "}
                      <Button type="link" onClick={downloadTemplate}>
                        <DownloadOutlined /> Download DDR
                      </Button>
                      and prepare request file
                    </Timeline.Item>
                    <Timeline.Item >
                      <Typography.Text strong>Step 2</Typography.Text>{" "}
                        Send email to <a href={`mailto:${process.env.REACT_APP_DATA_REQUEST_EMAIL || 'b2border@emariner.net'}?subject=Data`}>{process.env.REACT_APP_DATA_REQUEST_EMAIL || 'b2border@emariner.net'}</a> with the request file as the attachment.
                    </Timeline.Item>
                    <Timeline.Item style={{ paddingBottom: 0}}>
                      <Typography.Text strong>Step 3</Typography.Text> Ensure the email subject is "Data" and has no other attachments other than the request file.
                    </Timeline.Item>
                  </Timeline>
                </div>
              }
              showIcon
              style={{ 
                marginBottom: '9px',
                paddingBottom: '0px',
                flex: 1,
              }}
            />
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col xl={24} xs={24}>
            <Table
              {...tableProps}
              columns={columns}
              rowKey="id"
              size="middle"
              scroll={{ x: 1200 }}
              sticky
              locale={{
                emptyText: 'No files found. Request updates to see details here.'
              }}
              pagination={{
                ...tableProps.pagination,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
              }}
            />
          </Col>
        </Row>
      </List>
  );
};